import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  Pagination,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { listarCartoes, excluirCartao } from '~/Services/Service';
import ModalCartao from './components/ModalCartao';

const PAGE_SIZE = 20;
const thSortStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

const Cartoes = () => {
  const [cartoes, setCartoes] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const [filtroDescricao, setFiltroDescricao] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('');
  const [ordenacaoCampo, setOrdenacaoCampo] = useState('Descricao');
  const [ordenacaoDir, setOrdenacaoDir] = useState('asc');

  const [showModal, setShowModal] = useState(false);
  const [cartaoSelecionado, setCartaoSelecionado] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const debounceRef = useRef(null);

  // Debounce do filtro de texto
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFiltroAtivo(filtroDescricao);
      setPagina(1);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filtroDescricao]);

  // Carrega lista sempre que filtro/sort/pagina/reloadTick mudar
  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      setLoading(true);
      try {
        const resp = await listarCartoes({
          descricao: filtroAtivo || undefined,
          order_by: ordenacaoCampo,
          order_dir: ordenacaoDir,
          page_size: PAGE_SIZE,
          page_number: pagina,
        });
        if (!ativo) return;
        setCartoes((resp && resp.itens) ? resp.itens : []);
        setTotal((resp && resp.total != null) ? resp.total : 0);
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      } finally {
        if (ativo) setLoading(false);
      }
    };
    carregar();
    return () => { ativo = false; };
  }, [filtroAtivo, ordenacaoCampo, ordenacaoDir, pagina, reloadTick]);

  const triggerReload = () => setReloadTick((t) => t + 1);

  const handleOrdenacao = (campo) => {
    if (ordenacaoCampo === campo) {
      setOrdenacaoDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrdenacaoCampo(campo);
      setOrdenacaoDir('asc');
    }
    setPagina(1);
  };

  const iconeOrdenacao = (campo) => {
    if (ordenacaoCampo !== campo) return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return ordenacaoDir === 'asc'
      ? <FaSortUp style={{ marginLeft: 4 }} />
      : <FaSortDown style={{ marginLeft: 4 }} />;
  };

  const tituloOrdenavel = (label, campo) => (
    <span
      style={thSortStyle}
      onClick={(e) => {
        e.stopPropagation();
        handleOrdenacao(campo);
      }}
    >
      {label}{iconeOrdenacao(campo)}
    </span>
  );

  const handleNovo = () => {
    setCartaoSelecionado(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setCartaoSelecionado(item);
    setShowModal(true);
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    if (!confirmado) {
      setConfirmacao({ show: false, item: null });
      return;
    }
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    try {
      await excluirCartao(item.codigo);
      toast.success('Cartão excluído com sucesso.');
      // edge case LICOES §1.4: deletar último item → voltar página anterior
      if (cartoes.length === 1 && pagina > 1) {
        setPagina((prev) => prev - 1);
      } else {
        triggerReload();
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  };

  // Fix-round PR#548: criação volta para pág 1 (PRD "novo item navega à página correta"),
  // edição mantém o contexto da listagem (não pula pra pág 1).
  const handleSalvar = ({ criado } = {}) => {
    setShowModal(false);
    if (criado) setPagina(1);
    triggerReload();
  };

  const handlePagina = (pg) => {
    if (pg < 1 || pg > totalPaginas) return;
    setPagina(pg);
  };

  // Round 2 PR#684/548: contador (N) e bloqueio visual quando GEs vinculadas
  // refletem RN-04 já na ação de exclusão (PRD: "botão Excluir mostra (N)
  // quando bloqueado"). Quantidade vem de `quantidade_ges` no listar.
  const renderAcoes = (_v, item) => {
    const qtd = item && item.quantidade_ges ? item.quantidade_ges : 0;
    const bloqueado = qtd > 0;
    const tituloExcluir = bloqueado
      ? `Excluir bloqueado — ${qtd} GE(s) vinculada(s)`
      : 'Excluir';
    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        <Button
          size="sm"
          variant="outline-primary"
          title="Editar"
          className="mr-1"
          onClick={() => handleEditar(item)}
        >
          <FaEdit />
        </Button>
        <Button
          size="sm"
          variant="outline-danger"
          title={tituloExcluir}
          disabled={bloqueado}
          onClick={() => handleExcluir(item)}
        >
          <FaTrash />
          {bloqueado && <span className="ml-1">({qtd})</span>}
        </Button>
      </div>
    );
  };

  const colunas = [
    {
      title: tituloOrdenavel('Código', 'Codigo'),
      key: 'codigo',
      width: '80px',
      ordenacao: false,
    },
    {
      title: tituloOrdenavel('Descrição', 'Descricao'),
      key: 'descricao',
      ordenacao: false,
    },
    {
      title: 'Taxa (%)',
      key: 'taxa',
      width: '100px',
      ordenacao: false,
      render: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '—'),
    },
    {
      title: 'Dias',
      key: 'dias',
      width: '80px',
      ordenacao: false,
      render: (v) => (v != null ? v : '—'),
    },
    {
      title: 'GEs Vinculadas',
      key: 'quantidade_ges',
      width: '110px',
      align: 'center',
      ordenacao: false,
      render: (v) => (v != null ? v : 0),
    },
    {
      title: 'Ações',
      key: '__acoes',
      width: '110px',
      align: 'center',
      ordenacao: false,
      render: (_v, item) => renderAcoes(_v, item),
    },
  ];

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const pages = [];
    const maxBtn = 5;
    let start = Math.max(1, pagina - Math.floor(maxBtn / 2));
    const end = Math.min(totalPaginas, start + maxBtn - 1);
    if (end - start < maxBtn - 1) start = Math.max(1, end - maxBtn + 1);

    pages.push(
      <Pagination.First key="first" disabled={pagina === 1} onClick={() => handlePagina(1)} />,
      <Pagination.Prev key="prev" disabled={pagina === 1} onClick={() => handlePagina(pagina - 1)} />
    );
    if (start > 1) pages.push(<Pagination.Ellipsis key="ell-start" disabled />);
    for (let i = start; i <= end; i++) {
      const pg = i;
      pages.push(
        <Pagination.Item key={pg} active={pg === pagina} onClick={() => handlePagina(pg)}>
          {pg}
        </Pagination.Item>
      );
    }
    if (end < totalPaginas) pages.push(<Pagination.Ellipsis key="ell-end" disabled />);
    pages.push(
      <Pagination.Next key="next" disabled={pagina === totalPaginas} onClick={() => handlePagina(pagina + 1)} />,
      <Pagination.Last key="last" disabled={pagina === totalPaginas} onClick={() => handlePagina(totalPaginas)} />
    );
    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2 flex-wrap">
        <small className="text-muted">
          Mostrando {Math.min((pagina - 1) * PAGE_SIZE + 1, total)} até{' '}
          {Math.min(pagina * PAGE_SIZE, total)} de {total} registros
        </small>
        <Pagination size="sm" className="mb-0">
          {pages}
        </Pagination>
      </div>
    );
  };

  if (loading && cartoes.length === 0 && pagina === 1) return <Loading />;

  return (
    <Container fluid className="px-3 py-3">
      <Header title="Cadastro de Cartões" />

      <Card className="shadow-sm mb-3">
        <Card.Body className="py-2">
          <Row className="align-items-end">
            <Col md={8} className="mb-2 mb-md-0">
              <Form.Label as="small" className="mb-1 font-weight-bold">Descrição</Form.Label>
              <InputGroup size="sm">
                <InputGroup.Prepend>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Filtrar por descrição..."
                  value={filtroDescricao}
                  onChange={(e) => setFiltroDescricao(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4} className="text-right">
              <Button size="sm" variant="primary" onClick={handleNovo}>
                <FaPlus className="mr-1" /> Novo Cartão
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header
          className="d-flex justify-content-between align-items-center py-2"
          style={{ backgroundColor: '#0747a6', color: '#fff' }}
        >
          <strong>
            Cartões
            <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              ({total} {total === 1 ? 'registro' : 'registros'})
            </span>
          </strong>
          {loading && <small style={{ opacity: 0.8 }}>Carregando...</small>}
        </Card.Header>
        <Card.Body className="p-0">
          {cartoes.length === 0 && !loading ? (
            <p className="text-muted text-center my-4">Nenhum cartão encontrado.</p>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <TabelaDinamica
                  colunas={colunas}
                  dados={cartoes}
                  striped
                  hover
                />
              </div>
              {renderPaginacao()}
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <ModalCartao
          show={showModal}
          onHide={() => setShowModal(false)}
          cartao={cartaoSelecionado}
          onSalvar={handleSalvar}
        />
      )}

      <ModalConfirmacao
        show={confirmacao.show}
        title="Excluir Cartão"
        message={
          confirmacao.item
            ? `Deseja excluir o cartão "${confirmacao.item.descricao}"? Esta ação não pode ser desfeita.`
            : 'Deseja excluir este cartão?'
        }
        onClose={handleConfirmarExclusao}
      />
    </Container>
  );
};

export default Cartoes;
